import React, { useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Button, Card, DatePicker, Modal, PageHeader, ProgressBar, Select, Tabs } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import StaffingTabs, { STAFFING_SUPPORTING_TEXT, staffingTabPathsShape } from "./components/StaffingTabs";
import AvailabilityByShow from "./components/AvailabilityByShow";
import AvailabilityByPerson from "./components/AvailabilityByPerson";
import { errorMessages } from "./components/staffingFormatters";
import { availabilityWindowShape, monthName, venueDay, venueTime, unanswered } from "./components/availabilityFormatters";

const DAY = 24 * 60 * 60 * 1000;

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });

const subline = (current) => {
  const month = monthName(current.month);
  if (current.status === "closed") return `Closed ${venueDay(current.closes_at)}. Answers are locked; staff can request changes until you publish.`;
  if (current.status === "upcoming") return `Opens ${venueDay(current.opens_at)} · closes ${venueDay(current.closes_at)}.`;
  return `Opened ${venueDay(current.opens_at)} · closes ${venueDay(current.closes_at)}. Staff answer for each ${month} show in the staff app.`;
};

export const windowCard = (current, now = Date.now()) => {
  if (current.status === "closed") {
    const next = current.next_opens_at && `${monthName(current.month, 1)} opens ${venueDay(current.next_opens_at)}`;
    return { big: "Closed", small: next || "" };
  }
  if (current.status === "upcoming") return { big: "Not open yet", small: `Opens ${venueDay(current.opens_at)}` };
  const days = Math.max(0, Math.ceil((Date.parse(current.closes_at) - now) / DAY));
  return { big: `${days} ${days === 1 ? "day" : "days"} left`, small: `Closes ${venueDay(current.closes_at)} at ${venueTime(current.closes_at)}` };
};

const Kpi = ({ label, value, valueColor, children }) => (
  <Card padding="md" style={{ flex: "1 1 220px" }}>
    <div style={textStyle(13, "text-tertiary", 500)}>{label}</div>
    <div style={{ ...textStyle(24, valueColor || "text-primary", 600), marginTop: 4 }}>{value}</div>
    <div style={{ marginTop: 6 }}>{children}</div>
  </Card>
);

Kpi.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, valueColor: PropTypes.string, children: PropTypes.node };

const lastDayOfMonth = (isoMonth) => {
  const [year, month] = isoMonth.split("-").map(Number);
  return new Date(year, month, 0);
};

const localDate = (iso) => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

const isoDate = (date) => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");

const StaffingAvailabilityApp = ({ tabPaths, windows, availabilityWindow, canManage, paths }) => {
  const [current, setCurrent] = useState(availabilityWindow);
  const [view, setView] = useState("show");
  const [reminded, setReminded] = useState({});
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState(null);
  const month = monthName(current.month);
  const open = current.status === "open";
  const { stats } = current;
  const pending = unanswered(current).filter((person) => !reminded[person.staff_member_id]);
  const card = windowCard(current);

  const refresh = () => client.get(paths.window).then(({ data }) => setCurrent(data));

  const remind = (people, { one = false } = {}) => {
    setBusy(true);
    const ids = people.map((person) => person.staff_member_id);
    client
      .post(paths.reminders, { staff_member_ids: ids })
      .then(({ data }) => {
        const texted = data.reminded_staff_member_ids;
        setReminded((previous) => ({ ...previous, ...Object.fromEntries(texted.map((id) => [id, true])) }));
        const missed = people.filter((person) => !texted.includes(person.staff_member_id));
        if (one) {
          const first = people[0].name.split(" ")[0];
          if (missed.length) toastError(`${first} couldn’t be texted. Check their phone number.`);
          else toastSuccess(`Reminder texted to ${first}.`);
        } else if (texted.length === 0) {
          toastError("Nobody could be texted. Check their phone numbers.");
        } else {
          toastSuccess(`Texted ${texted.length} ${texted.length === 1 ? "person who hasn’t" : "people who haven’t"} finished.${missed.length ? ` ${missed.length} couldn’t be texted.` : ""}`);
        }
      })
      .catch((error) => toastError(errorMessages(error)[0] || "Could not send reminders."))
      .finally(() => setBusy(false));
  };

  const extend = () => {
    if (busy) return;
    setBusy(true);
    client
      .patch(paths.window, { closes_on: isoDate(extending) })
      .then(({ data }) => {
        toastSuccess(`Deadline moved to ${venueDay(data.closes_at)}.`);
        setExtending(null);
        refresh().catch(() => toastError("Deadline moved. Reload to see the new numbers."));
      })
      .catch((error) => toastError(errorMessages(error)[0] || "Could not move the deadline."))
      .finally(() => setBusy(false));
  };

  const openExtend = () => {
    const next = localDate(current.closes_at);
    next.setDate(next.getDate() + 3);
    const last = lastDayOfMonth(current.month);
    setExtending(next > last ? last : next);
  };

  return (
    <>
      <div className="od-themed" style={{ padding: 24 }}>
        <PageHeader title="Staffing" supportingText={STAFFING_SUPPORTING_TEXT} />
        <div className="mt-3">
          <StaffingTabs current="availability" paths={tabPaths} />
        </div>

        <div className="d-flex flex-wrap align-items-start justify-content-between" style={{ gap: 16, marginBottom: 16 }}>
          <div style={{ flex: "1 1 360px", minWidth: 0 }}>
            <div style={textStyle(18, "text-primary", 600)}>{month} availability</div>
            <div style={{ ...textStyle(14, "text-tertiary"), marginTop: 4 }}>{subline(current)}</div>
          </div>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            {windows.length > 1 && (
              <div style={{ minWidth: 240 }}>
                <Select
                  size="sm"
                  aria-label="Availability window"
                  options={windows.map((candidate) => ({ value: candidate.id, label: `${monthName(candidate.month)} ${candidate.month.slice(0, 4)} · ${candidate.venue_name}` }))}
                  value={current.id}
                  onChange={(id) => {
                    if (Number(id) !== current.id) window.location.href = `${paths.page}?window_id=${id}`;
                  }}
                />
              </div>
            )}
            {open && canManage && (
              <>
                <Button variant="secondary" disabled={busy} onClick={openExtend}>
                  Extend deadline
                </Button>
                {stats.show_count > 0 && current.people.some((person) => person.remindable) && (
                  <Button variant="secondary" disabled={busy || pending.length === 0} onClick={() => remind(pending)}>
                    {pending.length ? `Remind ${pending.length} ${pending.length === 1 ? "person" : "people"}` : "Everyone reminded"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap" style={{ gap: 16, marginBottom: 24 }}>
          <Kpi label="Responses" value={`${stats.responded_count} of ${stats.respondent_count}`}>
            <ProgressBar value={stats.respondent_count ? Math.round((stats.responded_count / stats.respondent_count) * 100) : 0} label="none" />
          </Kpi>
          <Kpi label={`Shows in ${month}`} value={stats.show_count}>
            <span style={textStyle(13, "text-tertiary")}>{stats.hold_count ? `${stats.hold_count} on hold, included in case they confirm` : "Confirmed shows and holds"}</span>
          </Kpi>
          <Kpi label="Shows short on staff" value={stats.short_show_count} valueColor={stats.short_show_count ? "text-error" : undefined}>
            <span style={textStyle(13, "text-tertiary")}>Fewer yes answers than the template needs</span>
          </Kpi>
          <Kpi label="Window" value={card.big}>
            <span style={textStyle(13, "text-tertiary")}>{card.small}</span>
          </Kpi>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 12 }}>
          <Tabs
            variant="button-border"
            collapseBelow={0}
            items={[
              { value: "show", label: "By show" },
              { value: "person", label: "By person" },
            ]}
            value={view}
            onChange={setView}
          />
          <span style={textStyle(13, "text-tertiary")}>
            {view === "show" ? `Based on ${stats.responded_count} ${stats.responded_count === 1 ? "answer" : "answers"}${open ? " so far" : ""}` : "Staff who haven’t answered are listed first"}
          </span>
        </div>

        {view === "show" ? (
          <AvailabilityByShow availabilityWindow={current} />
        ) : (
          <AvailabilityByPerson availabilityWindow={current} reminded={reminded} canRemind={open && canManage} busy={busy} onRemind={(person) => remind([person], { one: true })} />
        )}
      </div>

      <Modal
        open={extending !== null}
        onClose={() => setExtending(null)}
        title="Extend deadline"
        description={`Availability closes at the end of the day you pick, venue time. Staff can answer until then.`}
        confirmLabel="Move deadline"
        onConfirm={extend}
        cancelLabel="Cancel"
        onCancel={() => setExtending(null)}
      >
        {extending && (
          <DatePicker
            id="availability-closes-on"
            label="New deadline"
            value={extending}
            min={localDate(current.closes_at)}
            max={lastDayOfMonth(current.month)}
            onChange={(date) => date && setExtending(date)}
          />
        )}
      </Modal>
      <ToastContainer icon={false} />
    </>
  );
};

StaffingAvailabilityApp.propTypes = {
  tabPaths: staffingTabPathsShape.isRequired,
  windows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, month: PropTypes.string.isRequired, venue_name: PropTypes.string.isRequired })).isRequired,
  availabilityWindow: availabilityWindowShape.isRequired,
  canManage: PropTypes.bool.isRequired,
  paths: PropTypes.shape({ page: PropTypes.string.isRequired, window: PropTypes.string.isRequired, reminders: PropTypes.string.isRequired }).isRequired,
};

export default StaffingAvailabilityApp;
